import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { CookieConsentService } from '../../services/cookie-consent.service';

interface PolicySection {
    id: string;
    titleKey: string;
}

/**
 * The Cookie Policy, served from inside each host app at /cookie-policy.
 *
 * Public and unguarded on purpose: the consent modal links to it from the
 * sign-in screen, so it has to be readable before anyone has signed in. It
 * opens in a new tab so that reading it never destroys a half-made choice in
 * the modal.
 *
 * The legal body is deliberately not run through the translation catalogues.
 * A machine-adjacent translation of a legal instrument is a liability, so the
 * page chrome is translated and the governing text stays in English, as
 * stated on the page itself.
 */
@Component({
    selector: 'app-cookie-policy-page',
    templateUrl: './cookie-policy-page.component.html',
    styleUrls: ['./cookie-policy-page.component.scss'],
    standalone: false
})
export class CookiePolicyPageComponent implements OnInit {
    /** Shown in the page header and standfirst, e.g. "OXM Admin". */
    @Input() appName = 'this application';
    /** Where "Back to sign in" points. */
    @Input() backRoute = '/login';

    @Input() includeChurnZero = false;
    @Input() includeMixpanel = true;
    @Input() includeProductFruits = true;

    /** Owned by Security & Compliance; the draft carries no effective date yet. */
    public readonly version = '1.0';
    public readonly lastUpdated = '3 September 2026';
    public readonly owner = 'Security & Compliance / Data Protection Office';

    /**
     * The path this page is served on, so the table of contents can build
     * `<path>#section` hrefs. A bare `#section` resolves against `<base
     * href="/">`, which the router matches as the empty route and redirects
     * to /login.
     */
    public pagePath = '/cookie-policy';

    public readonly sections: readonly PolicySection[] = [
        { id: 'scope', titleKey: 'Purpose and scope' },
        { id: 'what', titleKey: 'What are cookies' },
        { id: 'approach', titleKey: 'Our approach to consent' },
        { id: 'categories', titleKey: 'Cookie categories' },
        { id: 'inventory', titleKey: 'Cookies and services we use' },
        { id: 'providers', titleKey: 'Optional providers' },
        { id: 'manage', titleKey: 'Manage or withdraw your choice' },
        { id: 'basis', titleKey: 'Legal basis' },
        { id: 'international', titleKey: 'International access' },
        { id: 'changes', titleKey: 'Changes to this policy' }
    ];

    constructor(
        private cookieConsentService: CookieConsentService,
        private route: ActivatedRoute,
        location: Location
    ) {
        this.pagePath = location.path() || this.pagePath;
    }

    /**
     * Routed directly (`component: CookiePolicyPageComponent` in each app's
     * Routes array), so per-app branding cannot be passed as a template
     * @Input binding — it comes through route `data` instead. The @Input
     * defaults above still apply if a host ever embeds this component
     * directly rather than routing to it.
     */
    ngOnInit(): void {
        const data = this.route.snapshot.data;
        if (data.appName) this.appName = data.appName;
        if (data.backRoute) this.backRoute = data.backRoute;
        if (data.includeChurnZero !== undefined)
            this.includeChurnZero = data.includeChurnZero;
        if (data.includeMixpanel !== undefined)
            this.includeMixpanel = data.includeMixpanel;
        if (data.includeProductFruits !== undefined)
            this.includeProductFruits = data.includeProductFruits;
    }

    get hasCustomerSuccessCategory(): boolean {
        return this.includeChurnZero || this.includeProductFruits;
    }

    /** Section "Manage or withdraw your choice" should be a working control, not an instruction to go elsewhere. */
    public openPreferences(): void {
        this.cookieConsentService.requestReopen();
    }
}
